/**
 * Magnitud con unidad (widget `area`).
 *
 * Es un numerico con la unidad pegada al control: `84,50 m²`. La unidad se
 * dibuja dentro del campo y no en la etiqueta porque lo que hay que leer junto
 * al numero es la unidad de ese numero — "Superficie: 84,50" al lado de
 * "Coeficiente: 0,0345" invita a compararlos, y no son la misma cosa.
 *
 * La unidad sale de `options.unit` y cae en `m²`, que es el caso del que nacio
 * (superficie de una unidad funcional). El mismo widget sirve para kg, litros o
 * kWh sin tocar el frontend: es el modulo el que declara su unidad.
 *
 * Como todo campo con decimales, emite **string** cuando el tipo es `decimal`:
 * convertirlo a `number` reintroduciria el error de redondeo que el backend
 * evita serializandolo asi.
 */

import { controlClass } from '@/components/fields/controls';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { unitOf } from '@/components/fields/format';
import { fraccionDe, stepFor, useNumericBuffer } from '@/components/fields/numeric';
import type { FieldInputProps } from '@/components/fields/types';

const UNIDAD_POR_DEFECTO = 'm²';

export function AreaField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  const buffer = useNumericBuffer(value, onChange, {
    entero: false,
    decimal: meta.type === 'decimal',
    minFraccion: 0,
    maxFraccion: fraccionDe(meta),
  });
  const unidad = unitOf(meta, UNIDAD_POR_DEFECTO);
  const idUnidad = `${id}-unidad`;

  if (readonly) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  // La unidad se suma a la descripcion del control: quien no la ve, la escucha.
  const descripcion = describedBy === undefined || describedBy === '' ? idUnidad : `${describedBy} ${idUnidad}`;

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        inputMode="decimal"
        data-step={stepFor(meta)}
        className={controlClass(invalid, 'pr-12 text-right tabular-nums')}
        value={buffer.texto}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={descripcion}
        autoComplete="off"
        onFocus={buffer.alEntrar}
        onChange={(evento) => {
          buffer.alEscribir(evento.target.value);
        }}
        onBlur={buffer.alSalir}
      />
      <span
        id={idUnidad}
        className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-fg-faint"
      >
        {unidad}
      </span>
    </div>
  );
}
